import React from "react";
import { create } from "zustand";
import CommentModel from "../models/Comment";
import _ from "lodash";
import firebase from "../Firebase";
import { v4 } from "uuid";
import Annotation from "../models/Annotation";
import * as Sentry from "@sentry/react";
/*
 * The removed CommentsManager import is intentional: its registered DOM marks are not the
 * author manuscript's source of truth. Feedback has two independent data layers: annotation
 * marks in ProseMirror/Yjs anchor ranges, while SQL comment rows own bodies and status. History
 * rewinds the first, not the second.
 */
import { useSudowriteStore } from "./sudowriteStore";
import { canUndoCommentInEditor, getAnnotationRangeInEditor, undoCommentInEditor, UNDO_FAILURE_REASON } from "../utils/tiptapSuggestion";

const isNonOpenComment = (comment) => {
  const status = typeof comment?.status === "string" ? comment.status.trim().toLowerCase() : "";
  if (status === "resolved" || status === "ignored" || status === "accepted" || status === "declined") {
    return true;
  }
  return Boolean(comment?.resolvedAt || comment?.resolved_at || comment?.acceptedAt || comment?.accepted_at || comment?.declinedAt || comment?.declined_at);
};

/*
 * An annotation is a ProseMirror mark on text, not a separate paragraph/node or the comment
 * body itself. Its annotationId joins the range to a comment row. Overlapping annotations can
 * share a mark attribute containing space-separated IDs; splitting and deduplicating preserves
 * each logical comment even across multiple text nodes. null means no inspectable document; []
 * means an inspected document with no marks.
 */
export const getDocumentAnnotationIds = (tiptapEditor) => {
  const doc = tiptapEditor?.state?.doc;
  if (!doc || typeof doc.descendants !== "function") return null;

  const annotationIds = new Set();
  doc.descendants((node) => {
    (node?.marks || []).forEach((mark) => {
      if (mark?.type?.name !== "annotation") return;
      String(mark.attrs?.annotationId || "")
        .split(/\s+/)
        .map((id) => id.trim())
        .filter(Boolean)
        .forEach((id) => annotationIds.add(id));
    });
  });

  return [...annotationIds];
};

/*
 * Previously an empty global mark registry disabled filtering. After retaining historical rows,
 * that fallback would display open cards with no range in the restored text. Only a synced,
 * authoritative index now filters open cards; a ready empty index hides all open cards.
 * Resolved/accepted/declined rows deliberately survive this projection so their status tabs
 * still work after their marks disappear.
 */
export const getRelevantComments = (comments, documentAnnotationIds, documentAnnotationsReady) => {
  if (!documentAnnotationsReady) return comments;
  const visibleAnnotationIds = new Set(documentAnnotationIds || []);
  return comments.filter((comment) => {
    // Keep resolved/ignored items even after annotation marks are removed so status
    // counts and non-open filters remain accurate in the sidebar.
    if (isNonOpenComment(comment)) return true;
    return visibleAnnotationIds.has(String(comment.annotationId || "").trim());
  });
};

/*
 * The badge needs a full-document count, not the length of the currently loaded 50-row page.
 * Intersect current manuscript IDs with the server's complete set of open root-comment IDs. A
 * restored mark can belong to a resolved or ignored comment, so merely counting marks is also
 * wrong. The draft composer's newAnnotationId is not a saved comment and must not increase the
 * count.
 */
export const getVisibleOpenCommentCount = ({
  comments,
  documentAnnotationIds,
  documentAnnotationsReady,
  openCommentAnnotationIds,
  newAnnotationId,
  fallbackCount,
}) => {
  if (!documentAnnotationsReady) return fallbackCount;

  const visibleAnnotationIds = new Set(
    (documentAnnotationIds || []).filter((annotationId) => annotationId && annotationId !== newAnnotationId),
  );
  if (Array.isArray(openCommentAnnotationIds)) {
    /*
     * The server ID set may lag local create/reopen/resolve actions. Overlay loaded rows' current
     * status before intersecting, so the badge responds immediately without waiting for a counts
     * refetch. This changes the derived count only; it does not reopen or otherwise mutate
     * persisted comment rows.
     */
    const openAnnotationIds = new Set(openCommentAnnotationIds);
    // Reconcile optimistic/local status changes that have not reached the
    // counts endpoint yet (for example a newly created or reopened comment).
    (comments || []).forEach((comment) => {
      const annotationId = String(comment?.annotationId || "").trim();
      if (!annotationId) return;
      if (isNonOpenComment(comment)) {
        openAnnotationIds.delete(annotationId);
      } else {
        openAnnotationIds.add(annotationId);
      }
    });
    let visibleOpenCount = 0;
    visibleAnnotationIds.forEach((annotationId) => {
      if (openAnnotationIds.has(annotationId)) visibleOpenCount += 1;
    });
    return visibleOpenCount;
  }

  // Older server responses do not include open annotation IDs. Retain the
  // annotation-count cap until the exact status-aware list has loaded.
  /*
   * Compatibility path for a counts response without the new ID list: cap the old total by the
   * number of current marks. This is only an approximation, unlike the exact intersection above.
   * null means IDs are unavailable; an empty array means the server authoritatively reported
   * none. Before document sync even the mark cap is unsafe, hence the early fallback.
   */
  const numericFallback = Number(fallbackCount);
  if (Number.isFinite(numericFallback)) {
    return Math.min(numericFallback, visibleAnnotationIds.size);
  }
  return visibleAnnotationIds.size;
};

export const escapeCssAttributeValue = (value) => String(value).replace(/\\/g, "\\\\").replace(/"/g, '\\"');

export const getAnnotationIdSelector = (annotationId) => `span[data-annotation-id~="${escapeCssAttributeValue(annotationId)}"]`;

const getAnnotationSelectorScope = (DOMDocument) =>
  DOMDocument.getElementById?.("editorContainer") ? "#editorContainer " : "";

const getScopedAnnotationIdSelector = (DOMDocument, annotationId) =>
  `${getAnnotationSelectorScope(DOMDocument)}${getAnnotationIdSelector(annotationId)}`;

const buildAnnotationPositionMap = (DOMDocument, annotationIds) => {
  const map = new Map();
  if (!DOMDocument) return map;
  const targetAnnotationIds = Array.isArray(annotationIds)
    ? new Set(annotationIds.map((id) => String(id || "").trim()).filter(Boolean))
    : null;
  if (targetAnnotationIds && targetAnnotationIds.size === 0) return map;

  // Scope to the manuscript editor when present so annotation marks rendered
  // elsewhere in the document (e.g. Document History previews) are ignored.
  // Pages without #editorContainer (ShareLinkPage) keep the document-wide query.
  const queryRoot = DOMDocument.getElementById?.("editorContainer") || DOMDocument;
  const annotationElements = queryRoot.querySelectorAll("span[data-annotation-id]");
  let domIndex = 0;
  for (const el of annotationElements) {
    const ids = (el.getAttribute("data-annotation-id") || "").split(/\s+/);
    ids.forEach((id) => {
      const trimmed = String(id || "").trim();
      if (trimmed && (!targetAnnotationIds || targetAnnotationIds.has(trimmed)) && !map.has(trimmed)) {
        map.set(trimmed, domIndex);
      }
    });
    if (targetAnnotationIds && map.size === targetAnnotationIds.size) break;
    domIndex += 1;
  }
  return map;
};

const AGENT_MENTION_REGEX = /(^|[\s,;(>"'<])@sudowrite\b/i;
const COMMENT_STATUS = {
  OPEN: "open",
  RESOLVED: "resolved",
  IGNORED: "ignored",
};

const plainTextFromStoredCommentHtml = (html) => {
  // Mirrors server `plainTextFromCommentHtml` in commentThreadAgentReply for @mention checks.
  if (!html || typeof html !== "string") return "";
  const withoutTags = html.replace(/<[^>]+>/g, " ");
  return withoutTags.replace(/\s+/g, " ").trim();
};

const hasAccessToCommentThreadAgent = () => {
  const { subscriptionJSON, subscription } = useSudowriteStore.getState();
  if (typeof subscriptionJSON?.hasAccessToAgenticChat === "boolean") return subscriptionJSON.hasAccessToAgenticChat;
  if (typeof subscription?.hasAccessToAgenticChat === "boolean") return subscription.hasAccessToAgenticChat;
  return false;
};

const commentShouldTriggerSudowriteThreadReply = ({ content }) => {
  const plainContent = plainTextFromStoredCommentHtml(typeof content === "string" ? content : "");
  return AGENT_MENTION_REGEX.test(plainContent);
};

/**
 * Optimistic pending UI when the incoming reply has no @sudowrite yet the server would still evaluate
 * the untagged-follow-up judge (needs an earlier Sudowrite tag in-thread). Mirrors
 * threadHadPriorSudowriteMention in scheduleCommentThreadAgentReplyIfNeeded — not "any AI replied".
 */
const commentThreadEligibleForSudowriteAgentFollowupUi = ({ parent }) => {
  if (!parent || typeof parent !== "object") return false;

  const rows = [{ content: parent.content }, ...(Array.isArray(parent.replies) ? parent.replies : [])];

  return rows.some((row) => {
    if (!row?.content || typeof row.content !== "string") return false;
    const plain = plainTextFromStoredCommentHtml(row.content);
    return AGENT_MENTION_REGEX.test(plain);
  });
};

const commentShouldKeepThreadOpenAfterSubmit = ({ parent }) => !parent;

const makePendingSudowriteReply = ({ annotationId, parentId }) => ({
  id: `temp-ai-${Date.now()}`,
  displayName: "SUDOWRITE",
  content: "",
  annotationId,
  parentId,
  aiId: "pending-comment-thread-agent",
  type: "ai",
  createdAt: new Date(),
  updatedAt: new Date(),
  userId: null,
  _pendingSudowriteResponse: true,
  _pendingInsertedAtMs: Date.now(),
});

const normalizeCommentPhotoURL = (photoURL) => {
  if (typeof photoURL !== "string") return null;
  const trimmed = photoURL.trim();
  if (!trimmed || trimmed === "null" || trimmed === "undefined") return null;
  return trimmed;
};

const mergeSavedTopLevelComment = (comments, tempCommentId, savedComment) => {
  let insertedSavedComment = false;
  const mergedComments = comments.reduce((nextComments, comment) => {
    const isTempComment = comment?.id === tempCommentId;
    const isSavedComment = comment?.id === savedComment.id;
    if (!isTempComment && !isSavedComment) {
      nextComments.push(comment);
      return nextComments;
    }
    if (!insertedSavedComment) {
      nextComments.push(savedComment);
      insertedSavedComment = true;
    }
    return nextComments;
  }, []);

  if (!insertedSavedComment) {
    mergedComments.push(savedComment);
  }

  return mergedComments;
};

const removeCommentById = (comments, commentId) => {
  if (!Array.isArray(comments) || !commentId) return comments;

  return comments.reduce((nextComments, comment) => {
    if (comment?.id === commentId) {
      return nextComments;
    }

    const replies = Array.isArray(comment?.replies) ? comment.replies : [];
    const filteredReplies = replies.filter((reply) => reply?.id !== commentId);
    nextComments.push(filteredReplies.length === replies.length ? comment : { ...comment, replies: filteredReplies });
    return nextComments;
  }, []);
};

const isRealAiReply = (reply) => Boolean((reply?.type === "ai" || reply?.aiId) && !reply?._pendingSudowriteResponse);

const getCommentThreadInReplyToId = (reply) => {
  const meta = reply?.metadata;
  if (!meta) return "";
  if (typeof meta === "string") {
    try {
      const parsed = JSON.parse(meta);
      return String(parsed?.commentThreadInReplyToId || "");
    } catch {
      return "";
    }
  }
  if (typeof meta === "object") {
    return String(meta?.commentThreadInReplyToId || "");
  }
  return "";
};

const parseReplyTimestamp = (reply) => {
  const raw = reply?.createdAt;
  if (!raw) return 0;
  const ms = new Date(raw).getTime();
  return Number.isFinite(ms) ? ms : 0;
};

const RECENT_REPLY_PRESERVE_WINDOW_MS = 2 * 60 * 1000;
const PENDING_REPLY_PRESERVE_WINDOW_MS = 3 * 60 * 1000;

const shouldPreserveMissingReply = (reply, incomingReplies, nowMs) => {
  if (!reply) return false;
  if (reply._pendingSudowriteResponse) {
    const incomingHasRealAiReply = Array.isArray(incomingReplies) ? incomingReplies.some((r) => isRealAiReply(r)) : false;
    if (incomingHasRealAiReply) return false;
    const insertedMs = Number(reply._pendingInsertedAtMs || parseReplyTimestamp(reply) || 0);
    if (!insertedMs) return false;
    return nowMs - insertedMs <= PENDING_REPLY_PRESERVE_WINDOW_MS;
  }
  if (reply.type === "ai" || reply.aiId) return false;
  const createdMs = parseReplyTimestamp(reply);
  if (!createdMs) return false;
  return nowMs - createdMs <= RECENT_REPLY_PRESERVE_WINDOW_MS;
};

const preserveRecentMissingUserReplies = (incomingComments, previousComments = []) => {
  if (!Array.isArray(incomingComments) || incomingComments.length === 0) return incomingComments;
  if (!Array.isArray(previousComments) || previousComments.length === 0) return incomingComments;

  const previousById = new Map(previousComments.map((comment) => [comment.id, comment]));
  const nowMs = Date.now();

  return incomingComments.map((comment) => {
    const previousComment = previousById.get(comment.id);
    if (!previousComment) return comment;

    const incomingReplies = Array.isArray(comment.replies) ? comment.replies : [];
    const previousReplies = Array.isArray(previousComment.replies) ? previousComment.replies : [];
    if (previousReplies.length === 0) return comment;

    const incomingReplyIds = new Set(incomingReplies.map((reply) => reply?.id).filter(Boolean));
    const preservedReplies = previousReplies.filter((reply) => {
      if (!reply?.id || incomingReplyIds.has(reply.id)) return false;
      return shouldPreserveMissingReply(reply, incomingReplies, nowMs);
    });

    if (preservedReplies.length === 0) return comment;
    return { ...comment, replies: [...incomingReplies, ...preservedReplies] };
  });
};

const stripExpiredPendingSudowriteRepliesFromComments = (comments, nowMs) => {
  if (!Array.isArray(comments)) return comments;
  return comments.map((comment) => {
    const replies = Array.isArray(comment.replies) ? comment.replies : [];
    const filtered = replies.filter((reply) => {
      if (!reply?._pendingSudowriteResponse) return true;
      const insertedMs = Number(reply._pendingInsertedAtMs || parseReplyTimestamp(reply) || 0);
      if (!insertedMs) return false;
      return nowMs - insertedMs <= PENDING_REPLY_PRESERVE_WINDOW_MS;
    });
    return filtered.length === replies.length ? comment : { ...comment, replies: filtered };
  });
};

const filterFreshPendingSudowriteReplies = (pendingReplies, nowMs) => {
  if (!Array.isArray(pendingReplies) || pendingReplies.length === 0) return [];
  return pendingReplies.filter((p) => {
    const insertedMs = Number(p?._pendingInsertedAtMs || parseReplyTimestamp(p) || 0);
    if (!insertedMs) return false;
    return nowMs - insertedMs <= PENDING_REPLY_PRESERVE_WINDOW_MS;
  });
};

const mergePendingSudowriteReplies = (comments, pendingReplies = []) => {
  const nowMs = Date.now();
  const commentsSansExpired = stripExpiredPendingSudowriteRepliesFromComments(Array.isArray(comments) ? comments : [], nowMs);
  const freshPendingReplies = filterFreshPendingSudowriteReplies(pendingReplies, nowMs);

  if (!commentsSansExpired.length) {
    return { comments: Array.isArray(comments) ? comments : [], unresolvedPending: [] };
  }
  if (freshPendingReplies.length === 0) {
    return { comments: commentsSansExpired, unresolvedPending: [] };
  }

  const unresolvedPending = [];
  const mergedComments = commentsSansExpired.map((comment) => {
    const threadPending = freshPendingReplies.filter((p) => p.parentId === comment.id);
    if (threadPending.length === 0) return comment;

    const existingReplies = Array.isArray(comment.replies) ? comment.replies : [];
    let nextReplies = existingReplies;

    for (const pending of threadPending) {
      const insertedMs = Number(pending?._pendingInsertedAtMs || 0);
      const isExpiredPending = insertedMs > 0 && nowMs - insertedMs > PENDING_REPLY_PRESERVE_WINDOW_MS;
      if (isExpiredPending) {
        continue;
      }
      const pendingForCommentId = pending?._pendingForCommentId;
      const pendingForCommentCreatedAtMs = Number(pending?._pendingForCommentCreatedAtMs || 0);
      const hasAiReplyForTriggeringComment = pendingForCommentId ? existingReplies.some((reply) => isRealAiReply(reply) && getCommentThreadInReplyToId(reply) === String(pendingForCommentId)) : false;
      const hasAiReplyAfterTriggerComment =
        pendingForCommentCreatedAtMs > 0 ? existingReplies.some((reply) => isRealAiReply(reply) && parseReplyTimestamp(reply) >= pendingForCommentCreatedAtMs) : false;
      if (hasAiReplyForTriggeringComment || hasAiReplyAfterTriggerComment) {
        continue;
      }
      unresolvedPending.push(pending);
      const alreadyPresent = existingReplies.some((reply) => reply?.id === pending.id);
      if (!alreadyPresent) {
        nextReplies = [...nextReplies, pending];
      }
    }

    return nextReplies === existingReplies ? comment : { ...comment, replies: nextReplies };
  });

  return { comments: mergedComments, unresolvedPending };
};

const sortCommentsByDomPosition = (comments, DOMDocument, previousComments = []) => {
  if (!Array.isArray(comments) || comments.length === 0) return comments;
  const positionMap = buildAnnotationPositionMap(
    DOMDocument,
    comments.map((comment) => comment?.annotationId),
  );
  const previousIndexMap = new Map(previousComments.map((c, i) => [String(c.id), i]));

  return [...comments].sort((a, b) => {
    const posA = positionMap.get(String(a.annotationId || "").trim()) ?? Number.POSITIVE_INFINITY;
    const posB = positionMap.get(String(b.annotationId || "").trim()) ?? Number.POSITIVE_INFINITY;
    if (posA !== posB) return posA - posB;

    // Keep stable ordering across refreshes for ties/missing annotations.
    const prevA = previousIndexMap.get(String(a.id)) ?? Number.POSITIVE_INFINITY;
    const prevB = previousIndexMap.get(String(b.id)) ?? Number.POSITIVE_INFINITY;
    if (prevA !== prevB) return prevA - prevB;

    return String(a.id).localeCompare(String(b.id));
  });
};

const markCommentsByAnnotationStatus = (comments, annotationId, status, updatedAtIso = new Date().toISOString()) => {
  if (!Array.isArray(comments) || comments.length === 0) return comments;
  const normalizedStatus = status === COMMENT_STATUS.IGNORED ? COMMENT_STATUS.IGNORED : COMMENT_STATUS.RESOLVED;

  return comments.map((comment) => {
    if (comment?.annotationId !== annotationId) return comment;
    const nextComment = {
      ...comment,
      status: normalizedStatus,
      resolvedAt: comment?.resolvedAt ?? updatedAtIso,
      resolved_at: comment?.resolved_at ?? updatedAtIso,
    };

    if (normalizedStatus === COMMENT_STATUS.IGNORED) {
      nextComment.declinedAt = comment?.declinedAt ?? updatedAtIso;
      nextComment.declined_at = comment?.declined_at ?? updatedAtIso;
    }

    return nextComment;
  });
};

const markCommentsOpenByAnnotationId = (comments, annotationId) => {
  if (!Array.isArray(comments) || comments.length === 0) return comments;
  return comments.map((comment) => {
    if (comment?.annotationId !== annotationId) return comment;
    return {
      ...comment,
      status: COMMENT_STATUS.OPEN,
      resolvedAt: null,
      resolved_at: null,
      acceptedAt: null,
      accepted_at: null,
      declinedAt: null,
      declined_at: null,
    };
  });
};

const initialState = {
  comments: [],
  commentCounts: null,
  /*
   * Separate lightweight count metadata from paginated comment bodies. Unavailable is null rather
   * than [], allowing older responses and prefetch state to retain fallback counting instead of
   * falsely reporting zero.
   */
  openCommentAnnotationIds: null,
  commenter: null,
  showCleanCopyScreenOverlay: false,
  setToast: () => {
    console.warn("setToast not set");
  },
  showComments: false,
  commentsLoaded: false,
  showNewCommentBox: false,
  waitingForFeedbackComments: false, // whether we are anticipating new comments, so we can open with zero comments
  hoveredAnnotationIds: [],
  activeAnnotationIds: [],
  newCommentBoxLocationY: 0, // rename in uses
  newAnnotationId: null,
  tiptapEditor: null,
  DOMDocument: null, // The reference to the DOMDocument that the Annotations are rendered in
  documentId: null,
  displayName: "",
  treatAsAnonymous: false, // this is for cases when a user has signed up as anonymous but hasn't visited the main app yet so they dont have a subscription
  mutateComments: () => {}, // SWR mutate function for comments
  updateCommentCounts: null, // Registered by useComments to keep sidebar badge totals in sync
  isMutateCommentsReady: false, // true once the real SWR mutate is registered
  /** Epoch ms — EditorContainer skips scroll-driven card sync while Date.now() is before this (e.g. after deletion). */
  commentScrollSyncSuppressedUntil: null,
  // styles
  overlapStyle: "background:rgba(255,203,0,.36);border-bottom:2px solid rgb(255,203,0);padding-bottom:2px;transition:background 0.2s ease, border 0.2s ease",
  singleStyle: "background:rgba(255,203,0,.12);border-bottom:2px solid rgba(255,203,0,.35);padding-bottom:2px;transition:background 0.2s ease, border 0.2s ease",
  annotationTimeouts: {},
  annotationPersists: {}, // annotationId -> in-flight share-link create promise, so cancel can order after it
  cancelledAnnotationPersists: {}, // annotationId -> true when the draft was cancelled while its persist was in flight
  allComments: [],
  isDocumentOwner: false,
  isSharedDocument: false,
  pendingThreadAgentReplies: [],
  annotationUndoSnapshots: {},
  /** annotationId -> nested suppress count while Apply/Dismiss/Resolve intentionally unsets marks */
  markRemovalSuppressedIds: {},
  /*
   * There are two suppression scopes: the existing per-ID map protects intentional
   * Apply/Dismiss/Resolve operations, while the new counter protects a whole-document
   * replacement. Readiness is separate from the ID array because a just-created, not-yet-synced
   * editor can temporarily look empty even when the real manuscript is annotated.
   */
  /** Nested suppress count for bulk document replacements such as History restore. */
  markRemovalSideEffectsSuppressed: 0,
  /** Annotation IDs in the active manuscript. Ready is separate so an empty set is authoritative. */
  documentAnnotationIds: [],
  documentAnnotationsReady: false,
};

// I also want to be able to do the same thing the folders are doing and store
// comments locally and progressively update the front end
// could I update comments with SWR?
export const useCommentStore = create((set, get) => ({
  ...initialState,
  // Local mutations already reconcile temporary rows. Publish them through the same
  // visibility projection, but do not merge old replies back in as a fetch does.
  setCommentRows: (allComments) => {
    const { documentAnnotationIds, documentAnnotationsReady } = get();
    set({ allComments, comments: getRelevantComments(allComments, documentAnnotationIds, documentAnnotationsReady) });
  },

  setComments: (comments) => {
    const previousAllComments = get().allComments || [];
    const DOMDocument = get().DOMDocument;
    const commentsWithPreservedReplies = preserveRecentMissingUserReplies(comments || [], previousAllComments);
    const sortedAllComments = get().showComments
      ? sortCommentsByDomPosition(commentsWithPreservedReplies, DOMDocument, previousAllComments)
      : commentsWithPreservedReplies;
    const pendingReplies = get().pendingThreadAgentReplies || [];
    const { comments: mergedWithPending, unresolvedPending } = mergePendingSudowriteReplies(sortedAllComments, pendingReplies);
    set({
      /*
       * setComments now derives visible cards from the current document index, but preserves every
       * loaded row in allComments. Hiding is not deletion: those retained rows can become visible
       * again when another History version restores their marks.
       */
      comments: getRelevantComments(
        mergedWithPending,
        get().documentAnnotationIds,
        get().documentAnnotationsReady,
      ),
      allComments: mergedWithPending, // Store unfiltered comments in stable DOM/sidebar order
      pendingThreadAgentReplies: unresolvedPending,
    });
  },

  setShowCleanCopyScreenOverlay: (showCleanCopyScreenOverlay) => set({ showCleanCopyScreenOverlay }),

  setCommentsLoaded: (commentsLoaded) => set({ commentsLoaded }),

  setCommentCounts: (commentCounts) => set({ commentCounts }),

  /*
   * Expose a dedicated setter for useComments to carry the complete server open-ID set into both
   * count consumers. This does not replace allComments or determine which comment bodies have
   * been fetched.
   */
  setOpenCommentAnnotationIds: (openCommentAnnotationIds) => set({ openCommentAnnotationIds }),

  setShowComments: (showComments) => set({ showComments }),

  setShowNewCommentBox: (showNewCommentBox) => set({ showNewCommentBox }),

  setWaitingForFeedbackComments: (waitingForFeedbackComments) => set({ waitingForFeedbackComments }),

  setNewCommentBoxLocationY: (newCommentBoxLocationY) => set({ newCommentBoxLocationY }),

  setHoveredAnnotationIds: (hoveredAnnotationIds) => set({ hoveredAnnotationIds }),

  setMutateComments: (mutateComments) => set({ mutateComments, isMutateCommentsReady: true }),

  setUpdateCommentCounts: (updateCommentCounts) => set({ updateCommentCounts }),

  adjustCommentCountsForStatusChange(fromType, toType = null) {
    if (!fromType && !toType) return;
    if (fromType && toType && fromType === toType) return;

    const applyCountsUpdate = (currentCounts) => {
      if (!currentCounts || typeof currentCounts !== "object") return currentCounts;
      const nextCounts = {
        [COMMENT_STATUS.OPEN]: Number(currentCounts[COMMENT_STATUS.OPEN]) || 0,
        [COMMENT_STATUS.RESOLVED]: Number(currentCounts[COMMENT_STATUS.RESOLVED]) || 0,
        [COMMENT_STATUS.IGNORED]: Number(currentCounts[COMMENT_STATUS.IGNORED]) || 0,
      };
      if (fromType) {
        nextCounts[fromType] = Math.max(0, (nextCounts[fromType] || 0) - 1);
      }
      if (toType) {
        nextCounts[toType] = (nextCounts[toType] || 0) + 1;
      }
      return nextCounts;
    };

    const updater = get().updateCommentCounts;
    if (typeof updater === "function") {
      updater(applyCountsUpdate);
      return;
    }

    const nextCounts = applyCountsUpdate(get().commentCounts);
    if (nextCounts) {
      set({ commentCounts: nextCounts });
    }
  },

  withSuppressedMarkRemoval(annotationId, fn) {
    const trimmedId = String(annotationId || "").trim();
    if (!trimmedId) return fn();

    const current = get().markRemovalSuppressedIds || {};
    set({
      markRemovalSuppressedIds: {
        ...current,
        [trimmedId]: (current[trimmedId] || 0) + 1,
      },
    });

    const release = () => {
      const counts = { ...(get().markRemovalSuppressedIds || {}) };
      const next = (counts[trimmedId] || 1) - 1;
      if (next <= 0) delete counts[trimmedId];
      else counts[trimmedId] = next;
      set({ markRemovalSuppressedIds: counts });
    };

    try {
      const result = fn();
      if (result && typeof result.then === "function") {
        return result.finally(release);
      }
      release();
      return result;
    } catch (error) {
      release();
      throw error;
    }
  },

  /*
   * History setContent still executes ProseMirror transactions and mark-removal callbacks even
   * though TipTap suppresses its usual update event. Temporarily block the callbacks' SQL/status
   * side effects, not the document transaction itself. A counter supports nesting; both
   * synchronous throws and promise completion release the guard so subsequent ordinary editing
   * retains its old cleanup behavior. This guard is local to this tab, not Yjs transaction
   * metadata: a second writable tab is the explicitly deferred limitation.
   */
  withSuppressedMarkRemovalSideEffects(fn) {
    set({ markRemovalSideEffectsSuppressed: (get().markRemovalSideEffectsSuppressed || 0) + 1 });

    const release = () => {
      set({
        markRemovalSideEffectsSuppressed: Math.max(
          0,
          (get().markRemovalSideEffectsSuppressed || 1) - 1,
        ),
      });
    };

    try {
      const result = fn();
      if (result && typeof result.then === "function") {
        return result.finally(release);
      }
      release();
      return result;
    } catch (error) {
      release();
      throw error;
    }
  },

  async resolveCommentWhenAnnotationRemoved(annotationId) {
    const trimmedId = String(annotationId || "").trim();
    if (!trimmedId) return;

    // Draft inline comment the user is still composing — no DB row yet.
    if (trimmedId === get().newAnnotationId) return;

    // Apply/Dismiss/Resolve unset marks on purpose — don't race-delete the card.
    if ((get().markRemovalSuppressedIds || {})[trimmedId]) return;

    // History restore replaces the whole document. Missing marks in the restored
    // version control visibility but must not permanently mutate comment rows.
    /*
     * Normal annotation removal below still soft-deletes open AI comments and resolves open user
     * comments; these are different persisted states. During History replacement, a missing mark
     * should only hide the card, leaving its row available if a later restore brings that
     * annotationId back. The existing per-ID guard remains independent.
     */
    if ((get().markRemovalSideEffectsSuppressed || 0) > 0) return;

    const comment = (get().allComments || []).find((row) => row?.annotationId === trimmedId);
    if (!comment || isNonOpenComment(comment)) return;

    // AI Feedback cards without a highlight are unusable — delete them instead of
    // leaving resolved orphans in the sidebar. User comments still resolve.
    const isAiComment = Boolean(comment.aiId)
      || String(comment.type || "").trim().toLowerCase() === "ai";
    if (isAiComment) {
      const result = await get().deleteComment(comment.id, trimmedId);
      if (result?.success) {
        get().adjustCommentCountsForStatusChange(COMMENT_STATUS.OPEN, null);
      }
      return;
    }

    const result = await get().resolveComment(trimmedId);
    if (result?.success) {
      get().adjustCommentCountsForStatusChange(COMMENT_STATUS.OPEN, COMMENT_STATUS.RESOLVED);
    }
  },

  setCommentScrollSyncSuppressedUntil: (commentScrollSyncSuppressedUntil) => set({ commentScrollSyncSuppressedUntil }),

  setAnnotationUndoSnapshot: (annotationId, snapshot) =>
    set((state) => ({
      annotationUndoSnapshots: {
        ...(state.annotationUndoSnapshots || {}),
        [annotationId]: snapshot,
      },
    })),

  clearAnnotationUndoSnapshot: (annotationId) =>
    set((state) => {
      const existing = { ...(state.annotationUndoSnapshots || {}) };
      delete existing[annotationId];
      return { annotationUndoSnapshots: existing };
    }),

  setActiveAnnotationIds: (activeAnnotationIds) => {
    const state = get();
    const validAnnotationIds = state.comments.map((comment) => comment.annotationId);
    const filteredIds = activeAnnotationIds.filter((id) => validAnnotationIds.includes(id));
    set({ activeAnnotationIds: filteredIds });
  },

  setNewAnnotationId: (newAnnotationId) => set({ newAnnotationId }),

  setSetToast: (setToast) => set({ setToast }),

  /*
   * Re-scan the actual current ProseMirror document and immediately reproject the retained rows.
   * Ordinary updates refresh IDs without declaring sync complete; only the provider-sync or
   * already-synced attachment paths pass markReady. If there is no inspectable editor, return to
   * unknown rather than treating that as an authoritative empty manuscript.
   */
  refreshDocumentAnnotationIndex: ({ markReady = false } = {}) => {
    const documentAnnotationIds = getDocumentAnnotationIds(get().tiptapEditor);
    if (!documentAnnotationIds) {
      set({ documentAnnotationIds: [], documentAnnotationsReady: false });
      return;
    }
    set({
      documentAnnotationIds,
      documentAnnotationsReady: markReady || get().documentAnnotationsReady,
    });
    get().refreshComments();
  },

  /*
   * Editor instances are replaced on document navigation/rebuild. Detach the old update listener
   * and clear both IDs and readiness so the new document never borrows the previous one's
   * annotation truth. setContent is silent by default, so History also invokes the refresh
   * explicitly instead of relying solely on this listener.
   */
  setTiptapEditor: (tiptapEditor, { documentSynced = false } = {}) => {
    const currentEditor = get().tiptapEditor;

    // Remove old listener
    if (currentEditor && get().editorUpdateListener) {
      currentEditor.off("update", get().editorUpdateListener);
    }

    set({
      tiptapEditor,
      editorUpdateListener: null,
      documentAnnotationIds: [],
      documentAnnotationsReady: false,
    });

    // Add new listener
    if (tiptapEditor) {
      const listener = () => {
        // Small delay to ensure marks are updated
        setTimeout(() => {
          /*
           * The deferred refresh lets the editor's mark state settle, but navigation can replace the
           * editor before this timeout runs. Check instance identity before touching the shared store;
           * otherwise an old editor event could refresh or filter the new document's comments.
           */
          if (get().tiptapEditor !== tiptapEditor) return;
          get().refreshDocumentAnnotationIndex();
        }, 0);
      };

      tiptapEditor.on("update", listener);
      set({ editorUpdateListener: listener });
      /*
       * Provider sync can finish before the editor is attached to the store. This explicit
       * already-synced option closes that ordering gap; waiting only for a future synced event could
       * leave filtering permanently in its pre-sync fallback.
       */
      if (documentSynced) {
        get().refreshDocumentAnnotationIndex({ markReady: true });
      }
    }
  },

  setDOMDocument: (DOMDocument) => set({ DOMDocument }),

  /*
   * Reset annotation authority only on an actual document switch. A same-document filter change
   * must not discard the ready index. Resetting here also covers navigation ordering where
   * useComments learns the document ID separately from editor attachment.
   */
  setDocumentId: (documentId) => {
    const state = get();
    if (state.documentId === documentId) return;

    set({
      documentId,
      documentAnnotationIds: [],
      documentAnnotationsReady: false,
    });
  },

  setCommenter: (commenter) => set({ commenter }),

  setDisplayName: (displayName) => set({ displayName }),

  setTreatAsAnonymous: (treatAsAnonymous) => set({ treatAsAnonymous }),

  setIsDocumentOwner: (isDocumentOwner) => set({ isDocumentOwner }),

  setIsSharedDocument: (isSharedDocument) => set({ isSharedDocument }),

  addOptimisticAIComments: (createdComments, _passages, invocationId) => {
    if (!createdComments || createdComments.length === 0) return;

    const state = get();
    const currentComments = state.allComments;

    // Build optimistic comment objects that match the structure returned by CommentModel.getAll
    const optimisticComments = createdComments
      .map((created) => {
        if (!created.commentId || !created.content) return null;

        // Create metadata matching the AI comment structure
        const metadata = {
          source: "agentic_chat",
          ...(created.feedback_type ? { feedbackType: created.feedback_type } : {}),
          ...(created.feedback_category ? { feedbackCategory: created.feedback_category } : {}),
        };

        // Build an optimistic comment that matches the expected Comment structure
        // id, annotationId, aiId, content, original, suggestion, metadata, createdAt, etc.
        return {
          id: created.commentId,
          annotationId: created.annotationId || `optimistic-${created.commentId}`,
          aiId: invocationId || "agentic-chat",
          content: created.content,
          original: created.quoted_text || null,
          suggestion: created.suggestion || null,
          metadata,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          userId: null, // AI comments have no user
          parentId: null,
          replies: [],
          resolved: false,
          _optimistic: true, // Flag to identify optimistic comments
        };
      })
      .filter(Boolean);

    if (optimisticComments.length === 0) return;

    // Merge optimistic comments with existing, avoiding duplicates
    const existingIds = new Set(currentComments.map((c) => c.id));
    const newOptimisticComments = optimisticComments.filter((c) => !existingIds.has(c.id));

    if (newOptimisticComments.length === 0) return;

    const mergedComments = [...currentComments, ...newOptimisticComments];
    const DOMDocument = state.DOMDocument;
    const sortedMerged = sortCommentsByDomPosition(mergedComments, DOMDocument, currentComments);

    set({
      /*
       * Incrementally inserted/generated comments need the same visibility rule as a full fetch. Keep
       * the sorted merged rows in allComments and project only the visible list; otherwise new
       * batches could reintroduce orphan open cards despite the initial fetch being filtered.
       */
      comments: getRelevantComments(
        sortedMerged,
        state.documentAnnotationIds,
        state.documentAnnotationsReady,
      ),
      allComments: sortedMerged,
    });
  },

  createNewAnnotationId: () => {
    const newAnnotationId = `a${v4().slice(0, 8)}`;
    set({ newAnnotationId });
    return newAnnotationId;
  },

  _unsetAnnotation(annotationId) {
    // Suppress onMarkRemoved side-effects while we intentionally clear the mark
    // (resolve/dismiss/delete), so AI orphan-delete cannot race those flows.
    get().withSuppressedMarkRemoval(annotationId, () => {
      if (get().tiptapEditor.isEditable) {
        get().tiptapEditor.commands.unsetAnnotation(annotationId);
      } else {
        // send through server
        const pendingPersist = get().annotationPersists[annotationId];
        if (pendingPersist) {
          // The create POST for this annotation is still in flight — a delete sent now
          // can arrive first (a silent no-op) and strand an orphaned highlight once the
          // create lands. Flag the draft cancelled so an unsent create is skipped, and
          // delete only after a create that actually happened settles.
          set({ cancelledAnnotationPersists: { ...get().cancelledAnnotationPersists, [annotationId]: true } });
          pendingPersist.then(
            (created) => {
              if (created) Annotation.unset(get().documentId, annotationId);
            },
            () => {},
          );
        } else {
          Annotation.unset(get().documentId, annotationId);
        }
      }
    });
    // clear timeout for disappearing annotations debugging
    get().clearAnnotationTimeout(annotationId);
  },

  async initAnonUser() {
    try {
      const { user: authUser } = await firebase.auth().signInAnonymously();
      // Note: firebase.auth().onAuthStateChanged hook in App.js will set the AnonUser
      // set the name given on visiting the page to their displayName
      set({ commenter: authUser });
    } catch (error) {
      Sentry.captureException(error, {
        tags: {
          func: "initAnonUser",
        },
      });
    }
    return;
  },

  async submitComment(documentId, content, parent = null) {
    if (typeof content !== "string" || content.trim() === "") {
      return {
        success: false,
        message: "Comment cannot be empty.",
        createdCommentId: null,
        keepThreadOpen: false,
      };
    }

    const isATopLevelComment = !parent;
    const shouldKeepThreadOpen = commentShouldKeepThreadOpenAfterSubmit({ content, parent });
    let createdCommentId = null;
    let annotationId;
    let parentId;

    if (isATopLevelComment) {
      annotationId = get().newAnnotationId;
      parentId = null;
    } else {
      parentId = parent.id;
      annotationId = parent.annotationId;
    }
    let annotationSourceText = null;
    if (isATopLevelComment && annotationId) {
      const parts = [];
      get().tiptapEditor?.state?.doc?.descendants?.((node) => {
        if (!node.isText) return;
        const matchesAnnotation = node.marks?.some(
          (mark) => mark.type?.name === "annotation"
            && String(mark.attrs?.annotationId || "").split(/\s+/).includes(annotationId),
        );
        if (matchesAnnotation) parts.push(node.text);
      });
      annotationSourceText = parts.join("").trim() || null;
    }

    set({ showNewCommentBox: false });
    set({ commentScrollSyncSuppressedUntil: Date.now() + 2000 });

    const commenterPhotoURL = normalizeCommentPhotoURL(get().commenter?.photoURL);
    const tempComment = {
      id: `temp-${Date.now()}`,
      displayName: get().displayName,
      content,
      annotationId,
      parentId,
      aiId: null,
      type: "user",
      photoURL: commenterPhotoURL,
      replies: [],
      createdAt: new Date(),
      updatedAt: new Date(),
      userId: get().commenter?.uid,
    };

    // Get current comments and mutate function
    const currentComments = get().allComments;
    const mutateComments = get().mutateComments;

    const canUseCommentThreadAgent = !get().isSharedDocument && hasAccessToCommentThreadAgent();
    const shouldAddPendingSudowriteReply =
      canUseCommentThreadAgent && (commentShouldTriggerSudowriteThreadReply({ content, parent }) || commentThreadEligibleForSudowriteAgentFollowupUi({ parent }));
    const pendingSudowriteReply = shouldAddPendingSudowriteReply
      ? makePendingSudowriteReply({ annotationId, parentId: parentId || tempComment.id })
      : null;
    if (pendingSudowriteReply) {
      const currentPending = get().pendingThreadAgentReplies || [];
      set({ pendingThreadAgentReplies: [...currentPending, pendingSudowriteReply] });
      setTimeout(() => {
        set((state) => {
          const pendingThreadAgentReplies = (state.pendingThreadAgentReplies || []).filter((p) => p?.id !== pendingSudowriteReply.id);
          const stripPendingReply = (comment) => {
            const replies = Array.isArray(comment?.replies) ? comment.replies : [];
            const nextReplies = replies.filter((r) => r?.id !== pendingSudowriteReply.id);
            return nextReplies.length === replies.length ? comment : { ...comment, replies: nextReplies };
          };
          const allComments = (state.allComments || []).map(stripPendingReply);
          const comments = (state.comments || []).map(stripPendingReply);
          return { pendingThreadAgentReplies, allComments, comments };
        });
      }, PENDING_REPLY_PRESERVE_WINDOW_MS);
    }

    // Prepare optimistic data
    let optimisticData;
    if (parentId) {
      optimisticData = currentComments.map((comment) =>
        comment.id === parentId
          ? {
              ...comment,
              replies: [...comment.replies, tempComment, ...(pendingSudowriteReply ? [pendingSudowriteReply] : [])],
            }
          : comment,
      );
    } else {
      optimisticData = [
        ...currentComments,
        pendingSudowriteReply
          ? { ...tempComment, replies: [pendingSudowriteReply] }
          : tempComment,
      ];
    }

    if (!firebase.auth().currentUser) {
      await get().initAnonUser();
    }

    try {
      await mutateComments(
        async (currentData) => {
          if (!Array.isArray(currentData)) {
            currentData = [];
          }
          const result = await CommentModel.create({
            documentId,
            content,
            annotationId,
            parentId,
            annotationSourceText,
            disableCommentThreadAgent: !canUseCommentThreadAgent,
          });
          const resultWithPhoto = {
            ...result,
            photoURL: normalizeCommentPhotoURL(result?.photoURL) || tempComment.photoURL || null,
          };
          createdCommentId = result?.id || null;
          const pendingWithTracking = pendingSudowriteReply
            ? {
                ...pendingSudowriteReply,
                parentId: parentId || resultWithPhoto.id,
                _pendingForCommentId: resultWithPhoto.id,
                _pendingForCommentCreatedAtMs: parseReplyTimestamp(resultWithPhoto) || Date.now(),
              }
            : null;

          if (pendingWithTracking) {
            const currentPending = get().pendingThreadAgentReplies || [];
            set({
              pendingThreadAgentReplies: currentPending.map((p) => (p?.id === pendingWithTracking.id ? pendingWithTracking : p)),
            });
          }

          const resultWithPending = !parentId && pendingWithTracking
            ? {
                ...resultWithPhoto,
                replies: [...(Array.isArray(resultWithPhoto.replies) ? resultWithPhoto.replies : []), pendingWithTracking],
              }
            : resultWithPhoto;
          // SWR may hold only the latest filtered server page while the store
          // retains historical rows. Reconcile against live rows, including the
          // optimistic item and any replies that arrived while create was pending.
          currentData = get().allComments;
          const updatedData = parentId
            ? currentData.map((comment) => {
                if (comment.id !== parentId) return comment;

                const existingReplies = Array.isArray(comment.replies) ? comment.replies : [];
                const filteredReplies = existingReplies.filter((reply) => reply?.id !== tempComment.id && reply?.id !== resultWithPhoto.id && !reply?._pendingSudowriteResponse);
                return {
                  ...comment,
                  replies: [...filteredReplies, resultWithPhoto, ...(pendingWithTracking ? [pendingWithTracking] : [])],
                };
              })
            : mergeSavedTopLevelComment(currentData, tempComment.id, resultWithPending);
          get().setCommentRows(updatedData);
          return updatedData;
        },
        {
          optimisticData: (_currentData) => {
            get().setCommentRows(optimisticData);
            return optimisticData;
          },
          rollbackOnError: true,
          revalidate: false,
        },
      );

      set({ newAnnotationId: null });
      return {
        success: true,
        message: "Comment saved successfully.",
        createdCommentId,
        keepThreadOpen: shouldKeepThreadOpen,
      };
    } catch (error) {
      set((state) => ({
        comments: removeCommentById(state.comments, tempComment.id),
        allComments: removeCommentById(state.allComments, tempComment.id),
      }));
      if (pendingSudowriteReply) {
        set({
          pendingThreadAgentReplies: (get().pendingThreadAgentReplies || []).filter((p) => p?.id !== pendingSudowriteReply.id),
        });
      }
      if (isATopLevelComment) {
        get()._unsetAnnotation(annotationId);
      }
      return {
        success: false,
        message: "Could not save your comment.",
        createdCommentId: null,
        keepThreadOpen: false,
      };
    }
  },

  async deleteComment(commentId, annotationId, parentId = null) {
    const isATopLevelComment = !parentId;
    const currentComments = get().allComments;
    const mutateComments = get().mutateComments;

    try {
      // Prepare optimistic data
      let optimisticData;
      if (isATopLevelComment) {
        optimisticData = currentComments.filter((comment) => comment.id !== commentId);
      } else {
        optimisticData = currentComments.map((comment) => (comment.id === parentId ? { ...comment, replies: comment.replies.filter((reply) => reply.id !== commentId) } : comment));
      }

      if (typeof commentId === "string" && commentId.startsWith("temp-")) {
        get().setCommentRows(optimisticData);
        if (isATopLevelComment) {
          get()._unsetAnnotation(annotationId);
        }
        return { success: true };
      }

      await mutateComments(
        async (_currentData) => {
          const result = await CommentModel.delete(commentId);
          if (!result.success) throw new Error("Could not delete comment.");
          if (isATopLevelComment) {
            get()._unsetAnnotation(annotationId);
          }
          return optimisticData;
        },
        {
          optimisticData: (_currentData) => {
            get().setCommentRows(optimisticData);
            return optimisticData;
          },
          rollbackOnError: true,
          revalidate: false,
        },
      );

      return { success: true };
    } catch (error) {
      Sentry.captureMessage("Could not delete comment." + error, {
        tags: {
          func: "deleteComment",
          commentId,
          annotationId,
          parentId,
          userId: get().commenter?.uid,
        },
      });
      get().setToast("Could not delete comment.");
      return { success: false, message: "Could not delete comment." };
    }
  },

  async resolveComment(annotationId, options = {}) {
    const documentId = get().documentId;
    const currentAllComments = get().allComments;
    const mutateComments = get().mutateComments;
    const targetStatus = options.status === COMMENT_STATUS.IGNORED ? COMMENT_STATUS.IGNORED : COMMENT_STATUS.RESOLVED;
    const persist = options.persist !== false;
    const nowIso = new Date().toISOString();

    try {
      const optimisticAllData = markCommentsByAnnotationStatus(currentAllComments, annotationId, targetStatus, nowIso);

      const annotationRange = getAnnotationRangeInEditor(get().tiptapEditor, annotationId);
      if (annotationRange?.text) {
        get().setAnnotationUndoSnapshot(annotationId, { passage: annotationRange.text });
      }

      if (!persist) {
        get()._unsetAnnotation(annotationId);
        get().setCommentRows(optimisticAllData);
        mutateComments(optimisticAllData, false);
        return { success: true };
      }

      await mutateComments(
        async (_currentData) => {
          const result = await CommentModel.resolve(annotationId, documentId);
          if (result.success) {
            get()._unsetAnnotation(annotationId);
            get().setCommentRows(optimisticAllData);
            return optimisticAllData;
          }
          throw new Error("Could not resolve comment.");
        },
        {
          optimisticData: (_currentData) => {
            get().setCommentRows(optimisticAllData);
            return optimisticAllData;
          },
          rollbackOnError: true,
          revalidate: false,
        },
      );

      return { success: true };
    } catch (error) {
      get().clearAnnotationUndoSnapshot(annotationId);
      Sentry.captureMessage("Could not resolve comment." + error, {
        tags: {
          func: "resolveComment",
          annotationId,
          userId: get().commenter?.uid,
        },
      });
      get().setToast("Could not resolve comment.");
      return { success: false, message: "Could not resolve comment." };
    }
  },

  async undoResolvedComment(comment) {
    const annotationId = comment?.annotationId;
    if (!annotationId) {
      return { success: false, message: "Missing annotation id." };
    }
    const tiptap = get().tiptapEditor;
    const snapshotPassage = get().annotationUndoSnapshots?.[annotationId]?.passage;
    const precheck = canUndoCommentInEditor(tiptap, comment, { snapshotPassage });
    if (!precheck.canUndo) {
      const message = precheck.reason === UNDO_FAILURE_REASON.MISSING_EDITOR ? "Could not undo comment." : "We can't undo this comment because the document has changed.";
      return {
        success: false,
        message,
        reason: precheck.reason,
      };
    }

    try {
      const result = await CommentModel.reopen(comment.id);
      if (!result?.success) {
        throw new Error("Could not reopen comment.");
      }
      const undoResult = undoCommentInEditor(tiptap, comment, { snapshotPassage });
      if (!undoResult.success) {
        Sentry.captureMessage("Undo: reopen succeeded but editor restore failed", {
          tags: {
            func: "undoResolvedComment",
            annotationId,
            commentId: comment.id,
            userId: get().commenter?.uid,
            reason: undoResult.reason,
          },
        });
        get().setToast("This comment was reopened, but highlights could not be restored. Refresh if the editor looks wrong.");
      }

      const optimisticAllComments = markCommentsOpenByAnnotationId(get().allComments, annotationId);
      get().setCommentRows(optimisticAllComments);
      get().mutateComments(optimisticAllComments, false);
      get().clearAnnotationUndoSnapshot(annotationId);
      return { success: true };
    } catch (error) {
      Sentry.captureMessage("Could not undo comment." + error, {
        tags: {
          func: "undoResolvedComment",
          annotationId,
          commentId: comment.id,
          userId: get().commenter?.uid,
        },
      });
      get().setToast("Could not undo comment.");
      return { success: false, message: "Could not undo comment.", alreadyToasted: true };
    }
  },

  async cancelComment() {
    get()._unsetAnnotation(get().newAnnotationId);
    set(() => ({ showNewCommentBox: false, newAnnotationId: null }));
  },

  async updateComment(commentId, content, parentId = null) {
    const isATopLevelComment = !parentId;
    const currentComments = get().allComments;
    const mutateComments = get().mutateComments;
    const editedAt = new Date().toISOString();

    try {
      // Prepare optimistic data
      let optimisticData;
      if (isATopLevelComment) {
        optimisticData = currentComments.map((comment) => (comment.id === commentId ? { ...comment, content, updatedAt: editedAt, isEdited: true } : comment));
      } else {
        optimisticData = currentComments.map((comment) =>
          comment.id === parentId
            ? {
                ...comment,
                replies: comment.replies.map((reply) => (reply.id === commentId ? { ...reply, content, updatedAt: editedAt, isEdited: true } : reply)),
              }
            : comment,
        );
      }

      await mutateComments(
        async (_currentData) => {
          const result = await CommentModel.update(commentId, content);
          if (!result.success) {
            throw new Error("Failed to update comment");
          }
          get().setCommentRows(optimisticData);
          return optimisticData;
        },
        {
          optimisticData: (_currentData) => {
            get().setCommentRows(optimisticData);
            return optimisticData;
          },
          rollbackOnError: true,
          revalidate: false,
        },
      );

      return { success: true, message: "Comment updated successfully." };
    } catch (error) {
      Sentry.captureMessage("Could not update comment." + error, {
        tags: {
          func: "updateComment",
          commentId,
          userId: get().commenter?.uid,
        },
      });
      get().setToast("Could not update comment.");
      return { success: false, message: "Could not update comment." };
    }
  },

  revealAnnotation(annotationId) {
    const DOMDocument = get().DOMDocument;
    if (!DOMDocument?.head) return () => {};

    const visibleAnnotationIds = get().comments.map((comment) => comment.annotationId);
    const style = DOMDocument.createElement("style");

    try {
      DOMDocument.head.appendChild(style);
    } catch {
      return () => {};
    }
    if (!style.sheet) {
      style.parentNode?.removeChild(style);
      return () => {};
    }
    const styleSheet = style.sheet;
    // Scope hover/reveal styling to the manuscript editor when present so
    // annotation marks rendered elsewhere (Document History previews) don't paint.
    const scopePrefix = getAnnotationSelectorScope(DOMDocument);
    const selector = getScopedAnnotationIdSelector(DOMDocument, annotationId);
    const annotationMarks = Array.from(DOMDocument.querySelectorAll(selector));
    let hoverStyle = null;
    const removeHoverStyle = () => {
      hoverStyle?.parentNode?.removeChild(hoverStyle);
      hoverStyle = null;
    };
    const addHoverStyle = () => {
      if (hoverStyle?.parentNode) return;
      hoverStyle = DOMDocument.createElement("style");
      try {
        DOMDocument.head.appendChild(hoverStyle);
        hoverStyle.sheet?.insertRule(`${selector} { ${get().overlapStyle} }`, 0);
      } catch {
        removeHoverStyle();
      }
    };

    try {
      styleSheet.insertRule(`${selector} { ${get().singleStyle} }`, styleSheet.cssRules.length);
      styleSheet.insertRule(`${selector}:hover { ${get().overlapStyle} }`, styleSheet.cssRules.length);

      annotationMarks.forEach((mark) => {
        const annotationIds = (mark.getAttribute("data-annotation-id") || "").split(" ");
        const visibleAnnotationIdsOnMark = annotationIds.filter((id) => visibleAnnotationIds.includes(id));

        if (visibleAnnotationIdsOnMark.length > 1) {
          styleSheet.insertRule(`${scopePrefix}span[data-annotation-id="${escapeCssAttributeValue(annotationIds.join(" "))}"] { ${get().overlapStyle} }`, styleSheet.cssRules.length);
        }
        mark.addEventListener("pointerenter", addHoverStyle);
        mark.addEventListener("pointerleave", removeHoverStyle);
      });
    } catch {
      style.parentNode?.removeChild(style);
      return () => {};
    }

    // Cleanup: Remove the entire stylesheet when component unmounts
    return () => {
      annotationMarks.forEach((mark) => {
        mark.removeEventListener("pointerenter", addHoverStyle);
        mark.removeEventListener("pointerleave", removeHoverStyle);
      });
      removeHoverStyle();
      style.parentNode?.removeChild(style);
    };
  },

  hoverAnnotation(annotationId) {
    const DOMDocument = get().DOMDocument;
    if (!DOMDocument?.head) return () => {};
    const style = DOMDocument.createElement("style");
    try {
      DOMDocument.head.appendChild(style);
    } catch {
      return () => {};
    }
    const styleSheet = style.sheet;
    if (!styleSheet) {
      style.parentNode?.removeChild(style);
      return () => {};
    }
    const selector = getScopedAnnotationIdSelector(DOMDocument, annotationId);
    // Add the rule to our new stylesheet
    try {
      styleSheet.insertRule(`${selector} { ${get().overlapStyle} }`, styleSheet.cssRules.length);
    } catch {
      style.parentNode?.removeChild(style);
      return () => {};
    }
    // Cleanup: Remove the entire stylesheet when component unmounts
    return () => {
      style.parentNode?.removeChild(style);
    };
  },

  // Persist a share-link draft annotation without blocking the composer. The in-flight
  // promise is registered so _unsetAnnotation can order a cancel after the create
  // instead of racing it. Resolves true if the annotation was created on the server,
  // false if creation was skipped because the draft was cancelled first.
  persistDraftAnnotation({ annotationId, documentId, from, to, authenticate }) {
    const persist = (async () => {
      if (authenticate) await authenticate();
      if (get().cancelledAnnotationPersists[annotationId]) return false;
      await Annotation.set(documentId, from, to, annotationId);
      return true;
    })();
    set({ annotationPersists: { ...get().annotationPersists, [annotationId]: persist } });
    const cleanup = () => get().clearAnnotationPersist(annotationId);
    persist.then(cleanup, cleanup);
    return persist;
  },

  clearAnnotationPersist: (annotationId) => {
    const annotationPersists = { ...get().annotationPersists };
    const cancelledAnnotationPersists = { ...get().cancelledAnnotationPersists };
    delete annotationPersists[annotationId];
    delete cancelledAnnotationPersists[annotationId];
    set({ annotationPersists, cancelledAnnotationPersists });
  },

  setAnnotationTimeout: (annotationId, timeoutId) => {
    const currentTimeouts = get().annotationTimeouts;
    set({ annotationTimeouts: { ...currentTimeouts, [annotationId]: timeoutId } });
  },

  clearAnnotationTimeout: (annotationId) => {
    const currentTimeouts = get().annotationTimeouts;
    if (currentTimeouts[annotationId]) {
      clearTimeout(currentTimeouts[annotationId]);
      const newTimeouts = { ...currentTimeouts };
      delete newTimeouts[annotationId];
      set({ annotationTimeouts: newTimeouts });
    }
  },

  refreshComments: () => {
    const state = get();
    if (state.allComments) {
      const { comments: mergedWithPending, unresolvedPending } = mergePendingSudowriteReplies(state.allComments, state.pendingThreadAgentReplies || []);
      set({
        /*
         * A mark-index refresh must rederive visibility from all retained rows, not just the currently
         * visible subset. This is how restoring an older annotated version makes its already-loaded
         * comment card reappear without resurrecting or recreating any SQL row.
         */
        comments: getRelevantComments(
          mergedWithPending,
          state.documentAnnotationIds,
          state.documentAnnotationsReady,
        ),
        allComments: mergedWithPending,
        pendingThreadAgentReplies: unresolvedPending,
      });
    }
  },
}));

export const withCommentStore = (BaseComponent) => {
  const MemoizedBaseComponent = React.memo(BaseComponent, (prev, next) => {
    return _.isEqual(prev, next);
  });

  return (props) => {
    const selector = (state) => {
      return {
        hoveredAnnotationIds: state.hoveredAnnotationIds,
        setHoveredAnnotationIds: state.setHoveredAnnotationIds,
        tiptapEditor: state.tiptapEditor,
        setTiptapEditor: state.setTiptapEditor,
        /*
         * The main editor receives store methods through this higher-order component, not directly from
         * the hook. Expose the new refresh method here so both provider onSynced callbacks can actually
         * mark the index ready; implementing the store method alone would leave that integration
         * unwired.
         */
        refreshDocumentAnnotationIndex: state.refreshDocumentAnnotationIndex,
        comments: state.comments,
        setComments: state.setComments,
        setDOMDocument: state.setDOMDocument,
        showComments: state.showComments,
        setShowComments: state.setShowComments,
        setWaitingForFeedbackComments: state.setWaitingForFeedbackComments,
        resolveCommentWhenAnnotationRemoved: state.resolveCommentWhenAnnotationRemoved,
      };
    };

    const selectedState = useCommentStore(selector);

    return React.createElement(MemoizedBaseComponent, {
      ...props,
      commentStore: selectedState,
    });
  };
};
